import { useState } from 'react';
import ReactPlayer from 'react-player';
import { PlayCircleIcon } from '@heroicons/react/24/outline';
import useConfig from 'services/config';
import { Image } from 'services/graphql';

import VideoBlockErrorBoundary from './VideoContentErrorBoundary';

import styles from './VideoContent.module.css';

interface VideoContentProps {
  video: { url: string; title: string; thumbnailUrl?: string };
  image?: Image;
}

export function VideoContent({ video, image }: VideoContentProps) {
  const { locale } = useConfig();
  const thumbnail = image?.url || video.thumbnailUrl;
  const [showPlayer, setShowPlayer] = useState(!thumbnail);
  const [hasPlaybackError, setHasPlaybackError] = useState(false);
  const previewStyle = thumbnail ? { backgroundImage: `url(${JSON.stringify(thumbnail)})` } : undefined;
  const fallback = (
    <a
      href={video.url}
      target="_blank"
      rel="noopener noreferrer"
      className={styles.preview}
      style={previewStyle}
      aria-label={video.title}
    >
      <PlayCircleIcon className={styles.play} />
      <span>{video.title}</span>
    </a>
  );

  if (!showPlayer) {
    return (
      <button
        type="button"
        className={styles.preview}
        style={previewStyle}
        aria-label={video.title}
        onClick={() => setShowPlayer(true)}
      >
        <PlayCircleIcon className={styles.play} />
      </button>
    );
  }

  return (
    <VideoBlockErrorBoundary fallback={fallback}>
      {hasPlaybackError ? (
        fallback
      ) : (
        <ReactPlayer
          src={video.url}
          light={false}
          playsInline
          width="100%"
          height="100%"
          autoPlay
          onError={() => setHasPlaybackError(true)}
          config={{
            youtube: {
              disablekb: 1,
              fs: 0,
              hl: locale,
              iv_load_policy: 3,
              rel: 0,
              color: 'white',
              cc_load_policy: 0,
            },
          }}
        />
      )}
    </VideoBlockErrorBoundary>
  );
}
